/** @prettier */
/*
 室外機デマンド制御ポップアップ
*/

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Switch,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  List,
  ListItem,
} from '@mui/material';
import {useEffect, useState} from 'react';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {getDemandSetting, patchDemandSetting} from '../../API/API_Collection';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';
import {format} from '../../define';
import * as React from 'react';
import {useTranslation} from 'react-i18next';

/**
 * デマンドデータをゲートウェイ名でソート
 * @param gatewayData ゲートウェイの情報
 * @return ソートされたゲートウェイの情報
 */
const sortUnitList = (
  gatewayData: apiIF.res.getDemandSetting['gateway-informations']
): apiIF.res.getDemandSetting['gateway-informations'] => {
  /** ユニット受信データ */
  //ソートで配列が破壊されるので配列をコピー
  let localGatewayData = gatewayData.concat();

  //ゲートウェイ名でソート
  localGatewayData.sort((a, b) => {
    //ゲートウェイ名で昇順ソート
    if (a['gateway-name'] !== b['gateway-name']) {
      return a['gateway-name'] < b['gateway-name'] ? -1 : 1;
    }
    return 0;
  });
  return localGatewayData;
};

//ユニット名・デマンドレベルの幅設定
const gridXsUnitName = 4.5;
const gridXsDemandControlLevel = 7.5;

/**
 * 室外機のデマンド設定ダイアログ
 * @param props
 * @returns ダイアログの表示画面
 */
const OutdoorUnitDemandControlDialog = (props: {
  allDemandInfo: apiIF.res.getDemandSetting; //デマンド情報
  setAllDemandInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getDemandSetting | undefined> //デマンド情報の設定関数
  >;
  isOpenOutdoorDialog: boolean; //ダイアログが開いているかどうかの判定
  setIsOpenOutdoorDialog: (arg0: boolean) => void; //ダイアログが開いているかどうかの判定の設定関数
  setUnitCount: React.Dispatch<
    React.SetStateAction<{
      ou: {
        lv0: number;
        lv1: number;
        lv2: number;
        lv3: number;
        all: number;
      };
      iu: {
        lv0: number;
        lv1: number;
        lv2: number;
        lv3: number;
        all: number;
      };
    }>
  >; //ユニット数の設定関数
  demandImplementation: true | false; //デマンド実施状況
}) => {
  //active/inactiveの設定
  const [controlOutdoorSwitching, setControlOutdoorSwitching] = useState(
    props.allDemandInfo['demand']['outdoor-unit']
  );

  //レベル１,２,３の室外機の電流制限値の設定
  const [ouCapacityLv1, setOUCapacityLv1] = useState(
    props.allDemandInfo['demand']['current-level1']
  );
  const [ouCapacityLv2, setOUCapacityLv2] = useState(
    props.allDemandInfo['demand']['current-level2']
  );
  const [ouCapacityLv3, setOUCapacityLv3] = useState(
    props.allDemandInfo['demand']['current-level3']
  );

  //TextBoxのエラー状態
  const [ouCapacityLv1Error, setOUCapacityLv1Error] = useState(false); //レベル１対象の室外機の電流制限
  const [ouCapacityLv2Error, setOUCapacityLv2Error] = useState(false); //レベル２対象の室外機の電流制限
  const [ouCapacityLv3Error, setOUCapacityLv3Error] = useState(false); //レベル３対象の室外機の電流制限
  const [ouCapacityLvCompError, setOUCapacityLvCompError] = useState(false); //レベル１，２，３の大小関係

  // ゲートウェイの情報
  const [demandGatewayInfo, setDemandGatewayInfo] = useState<
    apiIF.res.getDemandSetting['gateway-informations']
  >(sortUnitList(props.allDemandInfo['gateway-informations']));

  // 全ユニット一括のデマンドレベル設定用
  const [stateButtonAllDemandLevel, setStateButtonAllDemandLevel] = useState<
    'level0' | 'level1' | 'level2' | 'level3' | null
  >(null);

  const [saveBtnDisabled, setSaveBtnDisabled] = useState(false); //SAVEボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  const trans = useTranslation().t; //翻訳関数

  /**各レベルコンポーネントに使用する配列 */
  const levelAll: ('level1' | 'level2' | 'level3' | 'level0')[] = [
    'level1',
    'level2',
    'level3',
    'level0',
  ];

  /**各レベルに対応する表示 */
  const levelDisp = {
    level1: trans(
      'OutdoorUnitDemandControlDialog.unitSetting.table.data.level1'
    ),
    level2: trans(
      'OutdoorUnitDemandControlDialog.unitSetting.table.data.level2'
    ),
    level3: trans(
      'OutdoorUnitDemandControlDialog.unitSetting.table.data.level3'
    ),
    level0: trans(
      'OutdoorUnitDemandControlDialog.unitSetting.table.data.level0'
    ),
  };
  /**有効/無効に対応する表示 */
  const activeDisp = {
    active: trans('OutdoorUnitDemandControlDialog.modeSetting.dispActive'),
    inactive: trans('OutdoorUnitDemandControlDialog.modeSetting.dispInactive'),
  };
  /**
   * active/inactiveを切り替える
   * @param event スイッチを切り替えたイベント
   */
  const handleChangeAutoSwitching = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.target.checked
      ? setControlOutdoorSwitching('active')
      : setControlOutdoorSwitching('inactive');
  };

  //室外機のユニット数の計算
  useEffect(() => {
    props.setUnitCount(prevCount => ({
      ...prevCount,
      ou: countOutdoorUnit(props.allDemandInfo['gateway-informations']),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //電流制限値の判定
  useEffect(() => {
    setOUCapacityLvCompError(
      parseInt(ouCapacityLv1) <= parseInt(ouCapacityLv2) ||
        parseInt(ouCapacityLv2) <= parseInt(ouCapacityLv3)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouCapacityLv1, ouCapacityLv2, ouCapacityLv3]);

  /**
   * 全ユニットのデマンドレベルのボタン押下時処理
   * @param event ボタン押下時のイベント
   * @param demandLevel デマンドレベル
   */
  const onClickAllDemand = (
    event: React.MouseEvent<HTMLElement>,
    demandLevel: 'level0' | 'level1' | 'level2' | 'level3' | null
  ) => {
    /**
     * 全ユニットのデマンドレベルが連続して押された場合はnullが入力されるので
     * その場合は以前選択したデマンドレベルに再度設定するようにする。
     * demandLevelとstateButtonAllDemandLevelが共にnullとなる場合は初期値が
     * 設定されるが、そのような場合はあり得ない。
     */
    let targetDemandLevel: 'level0' | 'level1' | 'level2' | 'level3' = 'level0';
    if (demandLevel === null && stateButtonAllDemandLevel !== null) {
      targetDemandLevel = stateButtonAllDemandLevel;
    } else if (demandLevel !== null) {
      targetDemandLevel = demandLevel;
    }
    setStateButtonAllDemandLevel(targetDemandLevel);
    setDemandGatewayInfo(
      demandGatewayInfo.map(gatewayInfo => ({
        ...gatewayInfo,
        'outdoor-informations': gatewayInfo['outdoor-informations'].map(
          ouInfo => {
            return {...ouInfo, 'demand-level': targetDemandLevel};
          }
        ),
      }))
    );
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleCancel = () => {
    props.setIsOpenOutdoorDialog(false);
    /**
     * ポップアップを閉じると、適応していないデータは破棄
     * allDemandInfoはソートしていないので再度gateway-informationsをソート
     */
    setDemandGatewayInfo(
      sortUnitList(props.allDemandInfo['gateway-informations'])
    );
    setControlOutdoorSwitching(props.allDemandInfo['demand']['outdoor-unit']);
    setStateButtonAllDemandLevel(null);
    setOUCapacityLv1(props.allDemandInfo['demand']['current-level1']);
    setOUCapacityLv2(props.allDemandInfo['demand']['current-level2']);
    setOUCapacityLv3(props.allDemandInfo['demand']['current-level3']);
    //エラーが出ている状態でキャンセルを押した場合のためにエラーを解消する。
    setOUCapacityLv1Error(false);
    setOUCapacityLv2Error(false);
    setOUCapacityLv3Error(false);
    setOUCapacityLvCompError(false);
  };

  /**
   * SAVEボタン押下時処理
   */
  const onClickSave = () => {
    setSaveBtnDisabled(true);
    setAPIWait(true);

    const editData: apiIF.reqParameters.patchDemandSetting = {
      'outdoor-informations': [],
    };

    demandGatewayInfo.forEach(gatewayInfo => {
      gatewayInfo['outdoor-informations'].forEach(outUnitInfo => {
        const tmp = {
          'demand-level': outUnitInfo['demand-level'],
          'outdoor-id': outUnitInfo['outdoor-id'],
        };
        if (editData['outdoor-informations'] !== undefined) {
          editData['outdoor-informations'].push(tmp);
        }
      });
    });
    editData['outdoor-unit'] = controlOutdoorSwitching;
    editData['current-level1'] = ouCapacityLv1;
    editData['current-level2'] = ouCapacityLv2;
    editData['current-level3'] = ouCapacityLv3;
    patchDemandSetting(props.allDemandInfo['site-id'], editData)
      .then(() => {
        getDemandSetting(props.allDemandInfo['site-id']).then(response => {
          props.setAllDemandInfo(response);
          props.setIsOpenOutdoorDialog(false);
          setDemandGatewayInfo(sortUnitList(response['gateway-informations']));
          setStateButtonAllDemandLevel(null);
          props.setUnitCount(prevCount => ({
            ...prevCount,
            ou: countOutdoorUnit(response['gateway-informations']),
          }));
        });
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setSaveBtnDisabled(false);
        setAPIWait(false);
      });
  };

  /**
   * 室外機のデマンドレベル別のユニット数を計算する関数
   * @param demandGatewayInfo 室外機のデマンド情報
   * @returns 室外機のユニット数
   */
  const countOutdoorUnit = (
    demandGatewayInfo: apiIF.res.getDemandSetting['gateway-informations']
  ) => {
    let unitOutdoorCount = {
      lv0: 0,
      lv1: 0,
      lv2: 0,
      lv3: 0,
      all: 0,
    };
    if (props.allDemandInfo !== null) {
      //室外機
      demandGatewayInfo.forEach(gwInfo => {
        gwInfo['outdoor-informations'].forEach(ouInfo => {
          switch (ouInfo['demand-level']) {
            //Level0
            case 'level0':
              unitOutdoorCount.lv0++;
              break;
            //Level1
            case 'level1':
              unitOutdoorCount.lv1++;
              break;
            //Level2
            case 'level2':
              unitOutdoorCount.lv2++;
              break;
            //Level3
            case 'level3':
              unitOutdoorCount.lv3++;
              break;
          }
        });
      });
      unitOutdoorCount.all =
        unitOutdoorCount.lv0 +
        unitOutdoorCount.lv1 +
        unitOutdoorCount.lv2 +
        unitOutdoorCount.lv3;
    }
    return unitOutdoorCount;
  };

  return (
    <>
      <Dialog
        open={props.isOpenOutdoorDialog}
        aria-labelledby="form-dialog-title"
        maxWidth="md">
        <DialogTitle id="form-dialog-title">
          {trans('OutdoorUnitDemandControlDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {/* 有効/無効設定 */}
            <Box>
              <Grid item xs={'auto'}>
                *&nbsp;{trans('OutdoorUnitDemandControlDialog.caution')}
              </Grid>
            </Box>
            <Box>
              {trans('OutdoorUnitDemandControlDialog.modeSetting.title')}
              <Grid container alignItems={'center'} columnSpacing={1} ml={1}>
                <Grid item xs={'auto'}>
                  {trans('OutdoorUnitDemandControlDialog.modeSetting.item')}:
                </Grid>
                <Grid
                  item
                  xs={'auto'}
                  className="demandSetting__dialogContents__mode__value">
                  {activeDisp[controlOutdoorSwitching]}
                </Grid>
                <Grid item xs={'auto'}>
                  <Switch
                    disabled={props.demandImplementation === true}
                    checked={controlOutdoorSwitching === 'active'}
                    edge="end"
                    size="medium"
                    id="outdoor"
                    onChange={handleChangeAutoSwitching}
                  />
                </Grid>
              </Grid>
            </Box>
            {/* レベルのパラメーター設定 */}
            <Box>
              {trans('OutdoorUnitDemandControlDialog.parameterSetting.title')}
              <List className="demandSetting__dialogContents__parameterSetting">
                <ListItem>
                  <Grid container>
                    <Grid item xs="auto">
                      {trans(
                        'OutdoorUnitDemandControlDialog.parameterSetting.level1'
                      )}
                    </Grid>
                    <Grid
                      item
                      xs="auto"
                      className="demandSetting__dialogContents__parameterSetting__selectBox">
                      <ChkInputTextbox
                        disabled={props.demandImplementation === true}
                        setValue={setOUCapacityLv1}
                        setError={setOUCapacityLv1Error}
                        chkInputTxtOK={format.CAPACITY_PATTERN_OK}
                        type="text"
                        value={ouCapacityLv1}
                        id="mv-editCapacityLv1Input"
                        class="demandSetting__dialogContents__parameterSetting__textBox--capacity-level"
                        helperText=" "
                        errHelperText={trans(
                          'OutdoorUnitDemandControlDialog.parameterSetting.errHelperText'
                        )}
                        variant="standard"
                      />
                    </Grid>
                    <Grid item xs="auto">
                      {trans(
                        'OutdoorUnitDemandControlDialog.parameterSetting.rateCapacity'
                      )}{' '}
                    </Grid>
                  </Grid>
                </ListItem>
                <ListItem>
                  <Grid container>
                    <Grid item xs="auto">
                      {trans(
                        'OutdoorUnitDemandControlDialog.parameterSetting.level2'
                      )}
                    </Grid>
                    <Grid
                      item
                      xs="auto"
                      className="demandSetting__dialogContents__parameterSetting__selectBox">
                      <ChkInputTextbox
                        disabled={props.demandImplementation === true}
                        setValue={setOUCapacityLv2}
                        setError={setOUCapacityLv2Error}
                        chkInputTxtOK={format.CAPACITY_PATTERN_OK}
                        type="text"
                        value={ouCapacityLv2}
                        id="mv-editCapacityLv2Input"
                        class="demandSetting__dialogContents__parameterSetting__textBox--capacity-level"
                        helperText=" "
                        errHelperText={trans(
                          'OutdoorUnitDemandControlDialog.parameterSetting.errHelperText'
                        )}
                        variant="standard"
                      />
                    </Grid>
                    <Grid item xs="auto">
                      {trans(
                        'OutdoorUnitDemandControlDialog.parameterSetting.rateCapacity'
                      )}{' '}
                    </Grid>
                  </Grid>
                </ListItem>
                <ListItem>
                  <Grid container>
                    <Grid item xs="auto">
                      {trans(
                        'OutdoorUnitDemandControlDialog.parameterSetting.level3'
                      )}
                    </Grid>
                    <Grid
                      item
                      xs="auto"
                      className="demandSetting__dialogContents__parameterSetting__selectBox">
                      <ChkInputTextbox
                        disabled={props.demandImplementation === true}
                        setValue={setOUCapacityLv3}
                        setError={setOUCapacityLv3Error}
                        chkInputTxtOK={format.CAPACITY_PATTERN_OK}
                        type="text"
                        value={ouCapacityLv3}
                        id="mv-editCapacityLv3Input"
                        class="demandSetting__dialogContents__parameterSetting__textBox--capacity-level"
                        helperText=" "
                        errHelperText={trans(
                          'OutdoorUnitDemandControlDialog.parameterSetting.errHelperText'
                        )}
                        variant="standard"
                      />
                    </Grid>
                    <Grid item xs="auto">
                      {trans(
                        'OutdoorUnitDemandControlDialog.parameterSetting.rateCapacity'
                      )}{' '}
                    </Grid>
                  </Grid>
                </ListItem>
              </List>
              {ouCapacityLvCompError === true ? (
                <span className="demandSetting__dialogContents__parameterSetting__errorMassage">
                  {trans(
                    'OutdoorUnitDemandControlDialog.parameterSetting.errCapacity'
                  )}
                </span>
              ) : (
                <> </>
              )}
            </Box>
            {/** ユニット毎のレベル設定 */}
            <Box className="demandSetting__dialogContents__levelSetting">
              <Box>
                {trans('OutdoorUnitDemandControlDialog.unitSetting.title')}
              </Box>
              <Box className="demandSetting__dialogContents__levelSetting__description">
                {trans('OutdoorUnitDemandControlDialog.unitSetting.subTitle')}
              </Box>
              {/** テーブル */}
              <Box className="demandSetting__dialogContents__levelSetting__table">
                <Box className="demandSetting__dialogContents__levelSetting__table__frame">
                  {/** ヘッダ */}
                  <Grid
                    container
                    justifyContent="left"
                    className="demandSetting__dialogContents__levelSetting__table__frame__header">
                    <Grid
                      item
                      xs={gridXsUnitName}
                      className="demandSetting__dialogContents__levelSetting__table__frame__header__item">
                      {trans(
                        'OutdoorUnitDemandControlDialog.unitSetting.table.header.name'
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={gridXsDemandControlLevel}
                      className="demandSetting__dialogContents__levelSetting__table__frame__header__item">
                      {trans(
                        'OutdoorUnitDemandControlDialog.unitSetting.table.header.level'
                      )}
                    </Grid>
                  </Grid>
                  {/** 内容 */}
                  <Box>
                    {/** 全ユニット */}
                    <Grid
                      container
                      alignItems="center"
                      className="demandSetting__dialogContents__levelSetting__table__frame__data__all">
                      <Grid
                        item
                        xs={gridXsUnitName}
                        className="demandSetting__dialogContents__levelSetting__table__frame__data__all__category">
                        {trans(
                          'OutdoorUnitDemandControlDialog.unitSetting.table.data.all'
                        )}
                      </Grid>
                      <Grid
                        item
                        xs={gridXsDemandControlLevel}
                        className="demandSetting__dialogContents__levelSetting__table__frame__data__all__value">
                        <ToggleButtonGroup
                          disabled={props.demandImplementation === true}
                          value={stateButtonAllDemandLevel}
                          exclusive
                          onChange={onClickAllDemand}
                          fullWidth={true}
                          aria-label="OutdoorAllDemandLevel"
                          className="demandSetting__dialogContents__levelSetting__table__frame__data__button">
                          {levelAll.map(level => (
                            <ToggleButton
                              key={`mv-btnOutdoorAll${level}`}
                              value={level}
                              className="demandSetting__dialogContents__levelSetting__table__frame__data__button__buttonName">
                              {levelDisp[level]}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Grid>
                    </Grid>
                    {/** GW毎 */}
                    {demandGatewayInfo.map((gatewayInfo, gatewayIndex) => {
                      if (gatewayInfo['outdoor-informations'].length === 0) {
                        return <Box key={gatewayIndex}></Box>;
                      } else {
                        return (
                          <Box key={gatewayIndex}>
                            <Grid
                              container
                              alignItems="center"
                              className="demandSetting__dialogContents__levelSetting__table__frame__data__gateway">
                              <Grid
                                item
                                xs={12}
                                title={gatewayInfo['gateway-name']}
                                className="demandSetting__dialogContents__levelSetting__table__frame__data__category">
                                {gatewayInfo['gateway-name']}
                              </Grid>
                            </Grid>
                            {/** ユニット毎 */}
                            {gatewayInfo['outdoor-informations'].map(ouInfo => {
                              return (
                                <UnitSet
                                  key={ouInfo['outdoor-id']}
                                  unitName={ouInfo['outdoor-unit-name']}
                                  demandGatewayInfo={demandGatewayInfo}
                                  setDemandGatewayInfo={setDemandGatewayInfo}
                                  targetOutdoorID={ouInfo['outdoor-id']}
                                  stateButtonDemandLevel={
                                    ouInfo['demand-level']
                                  }
                                  levelAll={levelAll}
                                  levelDisp={levelDisp}
                                  demandImplementation={
                                    props.demandImplementation
                                  }
                                />
                              );
                            })}
                          </Box>
                        );
                      }
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          {/* CANCEL/SAVEボタンの設定） */}
          <SecondaryButton
            disabled={apiWait}
            onClick={handleCancel}
            buttonText={trans('OutdoorUnitDemandControlDialog.button.cancel')}
          />
          <PrimaryButton
            disabled={
              saveBtnDisabled ||
              ouCapacityLv1Error ||
              ouCapacityLv2Error ||
              ouCapacityLv3Error ||
              ouCapacityLvCompError ||
              props.demandImplementation === true
            }
            onClick={onClickSave}
            buttonText={trans('OutdoorUnitDemandControlDialog.button.save')}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={'mv-OutdoorDemandSettingErrDlgBtn'}
      />
    </>
  );
};

/**
 * 各ユニット毎のデマンドレベル設定用のボタンを表示する関数
 * @param props
 * @returns 表示画面
 */
const UnitSet = (props: {
  key: string; //キー
  unitName: string; //ユニット名
  demandGatewayInfo: apiIF.res.getDemandSetting['gateway-informations']; //デマンドの室外機の情報
  setDemandGatewayInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getDemandSetting['gateway-informations']> //デマンド情報の設定関数
  >;
  targetOutdoorID: string; //対象のユニットのID
  stateButtonDemandLevel: 'level0' | 'level1' | 'level2' | 'level3'; //デマンドレベルの情報
  levelAll: ('level1' | 'level2' | 'level3' | 'level0')[]; // Level0, level1, level2, level3の配列
  levelDisp: {level1: string; level2: string; level3: string; level0: string}; // ボタンに表示するレベルの文字列
  demandImplementation: true | false; //デマンド実施状況
}) => {
  /**
   * Levelボタンを押した時の処理
   * @param event ボタン押下時のイベント
   * @param selectedDemandLevel 選択したデマンドレベル
   */
  const onClickButton = (
    event: React.MouseEvent<HTMLElement>,
    selectedDemandLevel: 'level0' | 'level1' | 'level2' | 'level3' | null
  ) => {
    if (selectedDemandLevel !== null && props.demandGatewayInfo !== null) {
      props.setDemandGatewayInfo(
        props.demandGatewayInfo.map(gatewayInfo => ({
          ...gatewayInfo,
          'outdoor-informations': gatewayInfo['outdoor-informations'].map(
            ouInfo => {
              if (props.targetOutdoorID === ouInfo['outdoor-id']) {
                return {...ouInfo, 'demand-level': selectedDemandLevel};
              } else {
                return ouInfo;
              }
            }
          ),
        }))
      );
    }
  };

  return (
    <Grid
      container
      alignItems="center"
      className="demandSetting__dialogContents__levelSetting__table__frame__data__unit">
      <Grid
        item
        xs={gridXsUnitName}
        title={props.unitName}
        className="demandSetting__dialogContents__levelSetting__table__frame__data__category">
        {props.unitName}
      </Grid>
      <Grid item xs={gridXsDemandControlLevel}>
        {/**各デマンドレベルのトグルボタン */}
        <ToggleButtonGroup
          value={props.stateButtonDemandLevel}
          exclusive
          onChange={onClickButton}
          fullWidth={true}
          size={'small'}
          aria-label="OutdoorDemandLevel"
          className="demandSetting__dialogContents__levelSetting__table__frame__data__button">
          {props.levelAll.map(level => (
            <ToggleButton
              disabled={props.demandImplementation === true}
              key={`mv-btnOutdoor${level}`}
              value={level}
              className="demandSetting__dialogContents__levelSetting__table__frame__data__button__buttonName">
              {props.levelDisp[level]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>
    </Grid>
  );
};

export default OutdoorUnitDemandControlDialog;
